import { useMemo } from 'react';
import {
  ErrorMetadata,
  ErrorSeverity,
  ErrorCategory,
  classifyError,
  createDataLoadingError,
  createUserActionError,
  createValidationError
} from '@/lib/utils/errorTranslator';

export type ErrorDisplayType = 'banner' | 'inline' | 'modal' | 'toast' | 'none';

export interface ErrorDisplayConfig {
  displayType: ErrorDisplayType;
  metadata: ErrorMetadata;
}

/**
 * Determines the appropriate display method for an error based on its metadata
 *
 * Rules:
 * - CRITICAL errors → Modal (blocking)
 * - BLOCKING system/data-loading errors → Banner (persistent)
 * - BLOCKING user-action errors → Inline (contextual)
 * - NON_BLOCKING errors → Inline (contextual)
 * - Success messages → Toast (non-blocking)
 */
export function determineErrorDisplay(metadata: ErrorMetadata): ErrorDisplayType {
  // Critical errors always use modal
  if (metadata.severity === ErrorSeverity.CRITICAL) {
    return 'modal';
  }

  // System errors and data loading errors use banner
  if (
    metadata.category === ErrorCategory.SYSTEM ||
    metadata.category === ErrorCategory.DATA_LOADING
  ) {
    return 'banner';
  }

  // User action and validation errors use inline
  if (
    metadata.category === ErrorCategory.USER_ACTION ||
    metadata.category === ErrorCategory.VALIDATION
  ) {
    return 'inline';
  }

  // Default to inline for safety
  return 'inline';
}

/**
 * Hook to determine error display configuration
 *
 * @param error - The error to display (can be Error, ErrorMetadata, or unknown)
 * @param context - Optional context about where the error occurred
 * @param category - Optional error category override
 * @param recoveryActions - Optional recovery actions
 * @returns Error display configuration
 */
export function useErrorDisplay(
  error: Error | ErrorMetadata | unknown | null | undefined,
  context?: string,
  category?: ErrorCategory,
  recoveryActions?: { retry?: () => void | Promise<void>; dismiss?: () => void }
): ErrorDisplayConfig | null {
  return useMemo(() => {
    if (!error) {
      return null;
    }

    // If already ErrorMetadata, use it directly
    if (
      typeof error === 'object' &&
      error !== null &&
      'severity' in error &&
      'category' in error &&
      'message' in error
    ) {
      const metadata = error as ErrorMetadata;
      return {
        displayType: determineErrorDisplay(metadata),
        metadata
      };
    }

    // Otherwise, classify the error
    const metadata = classifyError(error, context, category, recoveryActions);
    return {
      displayType: determineErrorDisplay(metadata),
      metadata
    };
  }, [error, context, category, recoveryActions]);
}

/**
 * Helper hook for data loading errors
 */
export function useDataLoadingError(
  error: unknown | null | undefined,
  context?: string,
  retry?: () => void | Promise<void>
): ErrorDisplayConfig | null {
  return useMemo(() => {
    if (!error) {
      return null;
    }

    const metadata = createDataLoadingError(error, context, retry);
    return {
      displayType: 'banner',
      metadata
    };
  }, [error, context, retry]);
}

/**
 * Helper hook for user action errors
 */
export function useUserActionError(
  error: unknown | null | undefined,
  context?: string
): ErrorDisplayConfig | null {
  return useMemo(() => {
    if (!error) {
      return null;
    }

    const metadata = createUserActionError(error, context);
    return {
      displayType: 'inline',
      metadata
    };
  }, [error, context]);
}

/**
 * Helper hook for validation errors
 */
export function useValidationError(message: string | null | undefined): ErrorDisplayConfig | null {
  return useMemo(() => {
    if (!message) {
      return null;
    }

    const metadata = createValidationError(message);
    return {
      displayType: 'inline',
      metadata
    };
  }, [message]);
}
